import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, CheckCircle2, Loader2, Check } from "lucide-react";
import { trackEvent, getTrackingMeta, trackFormStart, trackQuoteSubmit, trackSalesPlanSubmitted, SERVICE } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCENT = "#BEF264";

const NEED_OPTIONS = [
  "New business website", "Redesign of an existing website", "Website with CRM", "Lead generation landing page",
  "Automated lead follow-up", "Sales pipeline setup", "Booking and consultation journey", "Other customer journey", "Not sure yet",
];

const CHANNEL_OPTIONS = ["Website form", "Email", "Phone calls", "WhatsApp", "Social media messages", "Referrals / walk-ins", "Very few enquiries yet"];
const CRM_OPTIONS = ["Yes, we use a CRM", "Spreadsheet only", "No CRM", "Not sure"];
const AFTER_OPTIONS = [
  "We reply manually by email or phone",
  "Enquiries go into a spreadsheet",
  "Enquiries are sent to WhatsApp",
  "They are recorded in our CRM",
  "It depends who sees it first",
  "Nothing structured yet",
];
const CHALLENGE_OPTIONS = [
  "Slow responses", "Inconsistent follow-up", "Can't track lead sources", "No view of the sales pipeline",
  "Website doesn't generate enquiries", "Too much manual admin",
];
const LAUNCH_OPTIONS = ["Within 30 days", "1 - 3 months", "3 - 6 months", "Not sure yet"];
const BUDGET_OPTIONS = ["Less than R10,000", "R10,000 - R25,000", "R25,000 - R50,000", "More than R50,000", "Not sure yet"];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const FIELD =
  "bg-white/[0.05] border border-white/15 text-white placeholder:text-white/35 rounded-xl h-12 focus-visible:border-[#BEF264] focus-visible:ring-2 focus-visible:ring-[#BEF264]/25 transition-shadow";

function SelectCard({ label, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`group relative text-left rounded-xl border px-4 py-3.5 transition-all duration-200 ${
        active ? "border-[#BEF264] bg-[#BEF264]/10" : "border-white/12 bg-white/[0.03] hover:border-white/25 hover:bg-white/[0.06]"
      }`}
    >
      <span className={`text-sm font-semibold leading-snug ${active ? "text-white" : "text-white/70"}`}>{label}</span>
      <span
        className={`absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full flex items-center justify-center transition-all ${
          active ? "opacity-100 scale-100" : "opacity-0 scale-75"
        }`}
        style={{ background: ACCENT }}
      >
        <Check className="h-3 w-3 text-[#0B1120]" />
      </span>
    </button>
  );
}

function Pill({ label, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
        active ? "border-[#BEF264] text-[#0B1120]" : "border-white/15 text-white/65 bg-white/[0.03] hover:border-white/30"
      }`}
      style={active ? { background: ACCENT } : undefined}
    >
      {label}
    </button>
  );
}

const STEPS = ["What you need", "Current process", "Your details"];

export default function SalesForm() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    need: "", channels: [], crm: "", after: "", challenges: [], message: "",
    first_name: "", last_name: "", company_name: "", website_url: "",
    business_email: "", mobile_number: "", launch: "", budget: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const startedRef = useRef(false);
  const inFlightRef = useRef(false);

  const set = (k, v) => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackEvent("form_start", { service: "website_app" });
      trackFormStart({ service: SERVICE.website, formName: "sales_system_recommendation_form" });
    }
    setForm((f) => ({ ...f, [k]: v }));
    setError("");
  };

  const toggle = (k, v) => set(k, form[k].includes(v) ? form[k].filter((x) => x !== v) : [...form[k], v]);

  const stepError = () => {
    if (step === 0 && !form.need) return "Please choose what you need.";
    if (step === 1) {
      if (!form.crm) return "Please tell us whether you use a CRM.";
      if (!form.after) return "Please tell us what happens after someone enquires.";
      if (!form.launch) return "Please choose a preferred timeframe.";
      if (!form.budget) return "Please choose a budget range.";
    }
    return null;
  };

  const goTo = (n) => {
    setError("");
    setStep(n);
    requestAnimationFrame(() => document.getElementById("request-plan")?.scrollIntoView({ block: "start" }));
  };
  const next = () => {
    const err = stepError();
    if (err) { setError(err); toast.error(err); return; }
    trackEvent("form_step", { service: "website_app", step: step + 1 });
    goTo(Math.min(step + 1, 2));
  };
  const back = () => goTo(Math.max(step - 1, 0));

  const validateFinal = () => {
    if (!form.first_name.trim()) return "First name is required.";
    if (!form.last_name.trim()) return "Last name is required.";
    if (!form.company_name.trim()) return "Business name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.business_email.trim())) return "A valid business email is required.";
    const digits = form.mobile_number.replace(/\D/g, "");
    if (digits.length < 9 || digits.length > 15) return "Please enter a valid mobile number, e.g. 071 234 5678.";
    if (form.website_url.trim() && !/^(https?:\/\/)?[^\s.]+\.[^\s]{2,}$/.test(form.website_url.trim())) return "Please enter a valid website address or leave it blank.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (inFlightRef.current || submitted) return;
    const err = validateFinal();
    if (err) { setError(err); toast.error(err); return; }
    setError("");

    const payload = {
      service_type: "website_app_creation",
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      company_name: form.company_name.trim(),
      website_url: form.website_url.trim() || null,
      business_email: form.business_email.trim(),
      mobile_number: form.mobile_number.trim(),
      what_to_build: form.need ? [form.need] : [],
      enquiry_channels: form.channels,
      has_crm: form.crm,
      problem_to_solve: form.after || null,
      problems: form.challenges,
      go_live_timing: form.launch,
      support_option: "not_sure",
      monthly_budget: form.budget,
      addons: [],
      message: form.message.trim() || null,
      tracking: getTrackingMeta(),
    };

    try {
      inFlightRef.current = true;
      setSubmitting(true);
      const res = await axios.post(`${API}/quote`, payload);
      trackSalesPlanSubmitted({ leadId: res.data?.id, projectType: form.need, budget: form.budget, goLive: form.launch });
      trackEvent("form_submit", { service: "website_app", what_to_build: [form.need], budget: form.budget });
      trackQuoteSubmit({ service: SERVICE.website, planId: "sales_system", addonIds: [], budget: form.budget, goLive: form.launch });
      setSubmitted(true);
      requestAnimationFrame(() => document.getElementById("request-plan")?.scrollIntoView({ block: "start" }));
      toast.success("Your Sales System request has been received");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error(err);
      toast.error("Could not submit your request. Please try again.");
    } finally {
      inFlightRef.current = false;
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section id="request-plan" data-testid="web-quote-form-success" className="relative py-24 sm:py-32 bg-sales-navy text-white">
        <div className="mx-auto max-w-2xl px-5 sm:px-8 text-center">
          <div className="h-16 w-16 rounded-full flex items-center justify-center mx-auto" style={{ background: `${ACCENT}22`, border: `1px solid ${ACCENT}55` }}>
            <CheckCircle2 className="h-8 w-8" style={{ color: ACCENT }} />
          </div>
          <h2 className="font-display mt-6 text-3xl sm:text-4xl font-black tracking-tight">
            Thank you — your Sales System request is in.
          </h2>
          <p className="mt-4 text-white/65 leading-relaxed">
            Rekisa will review your requirements and come back with a recommended plan and the next step.
            You'll also receive an immediate confirmation by email.
          </p>
          <div className="mt-8 grid sm:grid-cols-3 gap-3 text-left">
            {["We review your details", "We prepare your recommendation", "We contact you with next steps"].map((s, i) => (
              <div key={s} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                <div className="text-xs font-mono font-bold" style={{ color: ACCENT }}>0{i + 1}</div>
                <div className="mt-1.5 text-sm text-white/75 leading-snug">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="request-plan" aria-labelledby="request-plan-heading" data-testid="web-quote-form-section" className="relative py-24 sm:py-32 bg-sales-navy text-white overflow-hidden">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <div className="text-center mb-10">
          <div className="text-[11px] tracking-[0.2em] uppercase font-bold mb-3" style={{ color: ACCENT }}>Website and sales system quotation</div>
          <h2 id="request-plan-heading" className="font-display text-4xl sm:text-5xl font-black tracking-[-0.03em] leading-[1.05]">
            Get Your Rekisa Sales System Plan
          </h2>
          <p data-testid="sales-form-intro" className="mt-4 text-white/60 max-w-xl mx-auto">
            Tell us what your business needs customers to do and what should happen after they enquire. Rekisa will review
            your requirements and recommend an appropriate website and sales system implementation.
          </p>
        </div>

        {/* progress */}
        <div className="flex items-center gap-2 mb-8">
          {STEPS.map((label, i) => (
            <div key={label} className="flex-1">
              <div
                className="h-1.5 rounded-full transition-colors duration-300"
                style={{ background: i <= step ? ACCENT : "rgba(255,255,255,0.12)" }}
              />
              <div className={`mt-2 text-[11px] font-semibold ${i === step ? "text-white" : "text-white/40"}`}>
                <span className="font-mono mr-1">{i + 1}.</span>{label}{i === step && <span className="sr-only"> (current step)</span>}
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} noValidate data-testid="web-quote-form" className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-9">
          {step === 0 && (
            <div>
              <h3 className="font-display text-xl font-black mb-1">What do you need?</h3>
              <p className="text-sm text-white/50 mb-5">Choose the option closest to your goal.</p>
              <div className="grid sm:grid-cols-2 gap-2.5">
                {NEED_OPTIONS.map((opt) => (
                  <SelectCard key={opt} label={opt} active={form.need === opt} onClick={() => set("need", opt)} testid={`sales-form-need-${slug(opt)}`} />
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-7">
              <div>
                <h3 className="font-display text-xl font-black mb-1">Your current sales process</h3>
                <p className="text-sm text-white/50">A few quick taps — this helps us recommend the right setup.</p>
              </div>
              <fieldset>
                <legend className="text-sm font-semibold text-white/80 mb-2.5">How do enquiries currently reach you? <span className="text-white/40 font-normal">(select all that apply)</span></legend>
                <div className="flex flex-wrap gap-2">
                  {CHANNEL_OPTIONS.map((o) => (
                    <Pill key={o} label={o} active={form.channels.includes(o)} onClick={() => toggle("channels", o)} testid={`sales-form-channel-${slug(o)}`} />
                  ))}
                </div>
              </fieldset>
              <fieldset>
                <legend className="text-sm font-semibold text-white/80 mb-2.5">Do you use a CRM?*</legend>
                <div className="flex flex-wrap gap-2">
                  {CRM_OPTIONS.map((o) => (
                    <Pill key={o} label={o} active={form.crm === o} onClick={() => set("crm", o)} testid={`sales-form-crm-${slug(o)}`} />
                  ))}
                </div>
              </fieldset>
              <fieldset>
                <legend className="text-sm font-semibold text-white/80 mb-2.5">What happens after someone enquires?*</legend>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {AFTER_OPTIONS.map((o) => (
                    <SelectCard key={o} label={o} active={form.after === o} onClick={() => set("after", o)} testid={`sales-form-problem-${slug(o)}`} />
                  ))}
                </div>
              </fieldset>
              <fieldset>
                <legend className="text-sm font-semibold text-white/80 mb-2.5">Main challenges <span className="text-white/40 font-normal">(optional)</span></legend>
                <div className="flex flex-wrap gap-2">
                  {CHALLENGE_OPTIONS.map((o) => (
                    <Pill key={o} label={o} active={form.challenges.includes(o)} onClick={() => toggle("challenges", o)} testid={`sales-form-challenge-${slug(o)}`} />
                  ))}
                </div>
              </fieldset>
              <fieldset>
                <legend className="text-sm font-semibold text-white/80 mb-2.5">Preferred implementation timeframe*</legend>
                <div className="flex flex-wrap gap-2">
                  {LAUNCH_OPTIONS.map((o) => (
                    <Pill key={o} label={o} active={form.launch === o} onClick={() => set("launch", o)} testid={`sales-form-launch-${slug(o)}`} />
                  ))}
                </div>
              </fieldset>
              <fieldset>
                <legend className="text-sm font-semibold text-white/80 mb-2.5">Budget range*</legend>
                <div className="flex flex-wrap gap-2">
                  {BUDGET_OPTIONS.map((o) => (
                    <Pill key={o} label={o} active={form.budget === o} onClick={() => set("budget", o)} testid={`sales-form-budget-${slug(o)}`} />
                  ))}
                </div>
              </fieldset>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <h3 className="font-display text-xl font-black">Your details</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input data-testid="sales-form-first-name" aria-label="First name" className={FIELD} placeholder="First name*" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} autoComplete="given-name" />
                <Input data-testid="sales-form-last-name" aria-label="Last name" className={FIELD} placeholder="Last name*" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} autoComplete="family-name" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input data-testid="sales-form-company" aria-label="Business name" className={FIELD} placeholder="Business name*" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} autoComplete="organization" />
                <Input data-testid="sales-form-website-url" aria-label="Existing website URL" className={FIELD} type="text" inputMode="url" placeholder="Existing website URL (if any)" value={form.website_url} onChange={(e) => set("website_url", e.target.value)} autoComplete="url" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input data-testid="sales-form-email" aria-label="Business email" className={FIELD} type="email" placeholder="Business email*" value={form.business_email} onChange={(e) => set("business_email", e.target.value)} autoComplete="email" />
                <Input data-testid="sales-form-mobile" aria-label="Mobile number" className={FIELD} type="tel" inputMode="tel" placeholder="Mobile number*" value={form.mobile_number} onChange={(e) => set("mobile_number", e.target.value)} autoComplete="tel" />
              </div>
              <Textarea data-testid="sales-form-message" aria-label="Additional requirements" className={`${FIELD} h-auto min-h-[96px] py-3`} placeholder="Additional requirements (optional)" maxLength={2000} value={form.message} onChange={(e) => set("message", e.target.value)} />
              <p className="text-xs text-white/40 leading-relaxed">
                Your information will be used to assess your requirements and contact you about Rekisa Sales System.
                Marketing communication requires separate consent.
              </p>
            </div>
          )}

          {error && (
            <p role="alert" data-testid="sales-form-error" className="mt-6 rounded-xl border border-rose-400/40 bg-rose-500/10 px-4 py-2.5 text-sm text-rose-200">{error}</p>
          )}

          {/* nav */}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {step > 0 && (
              <button type="button" onClick={back} data-testid="sales-form-back"
                className="inline-flex items-center gap-1.5 rounded-full h-13 px-5 py-3 text-sm font-semibold text-white/70 border border-white/15 hover:bg-white/[0.06] transition-colors">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            )}
            {step < 2 ? (
              <Button key="sales-next" type="button" onClick={next} data-testid="sales-form-next"
                className="ml-auto rounded-full h-13 px-7 py-3 text-base font-bold text-[#0B1120] transition-all hover:brightness-105" style={{ background: ACCENT }}>
                Continue <ArrowRight className="ml-1 h-5 w-5" />
              </Button>
            ) : (
              <Button key="sales-submit" type="submit" disabled={submitting} data-testid="web-quote-form-submit"
                className="ml-auto rounded-full whitespace-normal h-auto min-h-12 px-6 py-3 text-center text-base font-bold text-[#0B1120] transition-[filter,opacity] hover:brightness-105 disabled:opacity-70" style={{ background: ACCENT }}>
                {submitting ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Submitting...</>) : (<>Get My Sales System Plan <ArrowRight className="ml-1 h-5 w-5" /></>)}
              </Button>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}
